import { Injectable, inject } from '@angular/core';
import { DialogService } from '@core/services/dialog.service';
import { ReleaseNotesDialogComponent } from './release-notes-dialog.component';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { ReleaseDialogData } from './interfaces';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { DvDialogComponent } from '@devinco/ui/dds';
import { VersionInformationDialogComponent } from '@features/version-information-dialog/version-information-dialog.component';
import { environment } from '@env/environment';
import { UserMetadataService } from '@core/services/user-metadata.service';

@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);
  private metaDataService = inject(UserMetadataService);

  openVersionInformationDialog(): void {
    this.dialog.open<ReleaseDialogData, VersionInformationDialogComponent>({
      size: 'md',
      title: 'General.VersionInformation',
      confirmText: 'General.Ok',
      contentComponent: VersionInformationDialogComponent,
      data: this.getReleaseData(),
    });
  }

  openReleaseNotesDialog(
    data?: ReleaseDialogData
  ): DvDialogComponent<ReleaseDialogData, ReleaseNotesDialogComponent> {
    return this.dialog.open<ReleaseDialogData, ReleaseNotesDialogComponent>({
      size: 'md',
      title: 'ReleaseDialog.Title',
      hideFooter: true,
      data: data || this.getReleaseData(),
      contentComponent: ReleaseNotesDialogComponent,
    });
  }

  checkForNewRelease(): void {
    const shouldDialogBeShown =
      localStorage.getItem(LocalStorageItems.ShowReleaseDialog) === 'true';
    if (shouldDialogBeShown) {
      const releaseData = this.getReleaseData();
      if (releaseData?.releaseNotes) {
        this.openReleaseNotesDialog(releaseData)
          .closed.pipe(take(1))
          .subscribe(() => {
            localStorage.setItem(LocalStorageItems.ShowReleaseDialog, 'false');
          });
      }
    }
  }

  private getReleaseData(): ReleaseDialogData | undefined {
    return this.getReleaseDataFromBuildName(
      this.getLatestReleaseVersionByPriority()
    );
  }

  /** getting release data by priority:
   * 1. returning release version from API
   * 2. getting last saved one from browser localStorage
   * 3. if both undefined -> grab from environment variables
   */
  private getLatestReleaseVersionByPriority(): string | null {
    return (
      this.metaDataService.getLastReleaseVersion() ||
      localStorage.getItem(LocalStorageItems.LatestRenewedVersion) ||
      environment.buildNumber
    );
  }

  private getReleaseDataFromBuildName(
    buildName: string | undefined | null
  ): ReleaseDialogData | undefined {
    const buildSplit = buildName?.split('_');
    // only dev env will not have release version
    const version = buildSplit?.[0]?.split('-')[1] || 'test version';
    const releaseDate = buildSplit?.[1]?.split('.')[0];
    const translationKey = `Release.${buildName}`;
    const translation = this.translate.instant(`Release.${buildName}`);

    if (!version || !releaseDate) return;

    return {
      version: version,
      date: this.formatReleaseDateString(releaseDate),
      releaseNotes: translation === translationKey ? undefined : translation,
    };
  }

  private formatReleaseDateString(date: string): string {
    return `${date.substring(6, 8)}.${date.substring(4, 6)}.${date.substring(
      0,
      4
    )}`;
  }
}
